<script setup>
import {APIService} from '@/core/util/services/api.service';
import {onMounted, reactive, ref, defineProps} from 'vue';
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';
import Status from '../constants/Status';
import MultiChips from '@ohrm/oxd/core/components/Input/MultiSelect/MultiSelectChips.vue';
import {CHART_COLORS} from '@ohrm/oxd/core/components/Chart/types';
import PieChart from '@ohrm/oxd/core/components/Chart/PieChart.vue';
import InformationResultsTable from '../components/InformationResultsTable.vue';

// Before
const props = defineProps({
  id: {type: Number, default: null},
});

const isLoading = ref(false);

const http = new APIService(window.appGlobal.baseUrl, 'api/v2/pim/employees');

const dataset = ref([]);

// Form
const form = reactive({
  id: props.id,
  code: '',
  version: '',
  dueDate: '',
  status: {},
  description: '',
  questions: [],
  files: [],
  results: {
    responses: 0,
    averageTime: '00:00',
  },
  selectedEmployee: null,
});

const encryptedId = ref('');

const employees = ref([]);

const employeeAnswers = ref([]);

onMounted(() => {
  isLoading.value = true;
  http
    .request({
      method: 'GET',
      url: `api/training/information/${props.id}`,
    })
    .then(response => {
      form.code = response.data.code;
      form.version = response.data.version;
      form.description = response.data.description;
      form.dueDate = response.data.due_date;
      form.status = Status.filter(item => item.id === response.data.status)[0];
      form.questions = response.data.questions;
      form.files = response.data.files;
      form.results = response.data.results;
      employees.value = response.data.employees;
      encryptedId.value = response.data.encrypted_id;

      dataset.value = [
        {
          value: response.data.results.responses,
          color: CHART_COLORS.COLOR_BLEU_DE_FRANCE,
          label: 'Answered',
        },
        {
          value: response.data.results.notAnswered,
          color: CHART_COLORS.COLOR_MEDIUM_ORCHID,
          label: 'Not answered',
        },
      ];
    })
    .finally(() => {
      isLoading.value = false;
    });

  http
    .getAll({
      includeEmployees: 'onlyCurrent',
    })
    .then(() => {
      // employees.value = response.data.data.map(employee => {
      //   return {
      //     id: employee.empNumber,
      //     label: `${employee.firstName} ${employee.middleName} ${employee.lastName}`,
      //   };
      // });
    });
});

const openFile = path => {
  window.open(`${window.appGlobal.publicPath}/${path}`, '_blank');
};

const onCancel = () => {
  window.location.assign(
    `${window.appGlobal.baseUrl}/training/viewInformation`,
  );
};

const getAnswerByEmployee = () => {
  if (form.selectedEmployee != null) {
    http
      .request({
        method: 'GET',
        url: `api/training/answers`,
        params: {
          informationId: props.id,
          employeeId: form.selectedEmployee.id,
        },
      })
      .then(response => {
        employeeAnswers.value = response.data;
      });
  } else {
    employeeAnswers.value = [];
  }
};

const openExportResults = () => {
  window.open(
    `${window.appGlobal.baseUrl}/training/information/${encryptedId.value}/export/results`,
    '_blank',
  );
};
</script>
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        <template>
          Information
        </template>
      </oxd-text>
      <oxd-form :loading="isLoading">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                :value="`INF-${form.id}`"
                label="Code"
                placeholder="Code"
                :disabled="true"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.code"
                label="Title"
                placeholder="Title"
                :disabled="true"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.dueDate"
                label="Due date"
                placeholder="Due date"
                :disabled="true"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.status"
                label="Status"
                value="Select"
                type="select"
                :options="Status"
                :disabled="true"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="form.description"
            :disabled="true"
            type="textarea"
            label="Description"
            placeholder="Description"
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-table-filter filter-title="Questions">
            <template v-for="question in form.questions" :key="question.id">
              <oxd-grid :cols="2" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <OxdLabel :label="question.title" />
                </oxd-grid-item>
                <oxd-grid-item>
                  <MultiChips :selected="question.options" :disabled="true" />
                </oxd-grid-item>
              </oxd-grid>
              <oxd-divider />
            </template>
          </oxd-table-filter>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-table-filter filter-title="Files">
            <div
              v-for="file in form.files"
              :key="file.label"
              class="flex gap-3 items-center"
            >
              <oxd-icon-button name="download" @click="openFile(file.value)" />
              <OxdLabel :label="file.label" />
            </div>
          </oxd-table-filter>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-table-filter filter-title="Results Overview">
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <OxdLabel label="Responses" />
                <br />
                <!--
                  <OxdLabel :label="`${form.results.responses} %`" />
                -->
                <PieChart
                  :data="dataset"
                  :aspect-ratio="false"
                  :custom-legend="true"
                  :custom-tooltip="true"
                  wrapper-classes="chart"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <OxdLabel label="Average time to complete" />
                <br />
                <OxdLabel :label="form.results.averageTime" />
              </oxd-grid-item>
              <oxd-grid-item>
                <OxdLabel label="Export results" />
                <oxd-icon-button
                  name="file-arrow-down-fill"
                  @click="openExportResults"
                />
              </oxd-grid-item>
            </oxd-grid>
            <InformationResultsTable :information-id="id" />
          </oxd-table-filter>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-table-filter filter-title="User results detail">
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="form.selectedEmployee"
                  label="Assigned to"
                  value="Select"
                  type="select"
                  :options="employees"
                  @update:modelValue="getAnswerByEmployee"
                />
              </oxd-grid-item>
              <oxd-grid :rows="2" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <OxdLabel label="Time to complete" />
                </oxd-grid-item>
                <oxd-grid-item>
                  <OxdLabel :label="employeeAnswers.timeToComplete" />
                </oxd-grid-item>
              </oxd-grid>
            </oxd-grid>
            <template
              v-for="question in employeeAnswers.questions"
              :key="question.id"
            >
              <oxd-grid-item>
                <oxd-input-group
                  :classes="{wrapper: '--status-grouped-field'}"
                  :label="question.value"
                  required
                >
                  <oxd-input-field
                    v-for="option in question.options"
                    :key="option.id"
                    :name="`radio_${question.id}`"
                    type="radio"
                    :checked="option.selected"
                    :option-label="option.value"
                    :disabled="true"
                  />
                </oxd-input-group>
              </oxd-grid-item>
            </template>
            <oxd-grid-item>
              <oxd-input-field
                :value="employeeAnswers.comment"
                :disabled="true"
                label="Comments"
                type="textarea"
              />
            </oxd-grid-item>
          </oxd-table-filter>
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions class="gap-3">
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-85 {
  width: 85%;
}

.w-20 {
  width: 20%;
}

.w-fit {
  width: fit-content;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.flex-column-direction {
  display: flex;
  flex-direction: column;
}

.gap-3 {
  gap: 3px;
}
.oxd-input-group__message {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
}
.oxd-text {
  font-family: 'Nunito Sans';
  margin-top: 0;
  margin-bottom: 0;
}

.chart {
  width: auto;
  height: 312px;
}
</style>
